import UnifiedNavHeader from "../../../../components/navigation/UnifiedNavHeader";
import React, {useCallback, useEffect, useState} from "react";
import {Header} from "../../../../components/dashboards/components/shared/Header";
import {BetaNotice} from "../components/BetaNotice";
import {AlertCircle} from "lucide-react";
import {volleyballDashPtBR} from "../../../../components/mappings/sports-dash-mappings/volleyball-dash.pt-br";
import {getBrazilianStatus} from "../../../../utils/detectBrazil";
import {AnimatePresence, motion} from "framer-motion";
import GamesView from "../components/GamesView";
import StandingsView from "../components/StandingsView";
import TeamsView from "../components/TeamsView";
import VolleyballService from "../services/volleyballService";
import DesktopNavigation, {TABS_ID} from "../components/DesktopNavigation";
import { USA } from "../constants/countries";
import {VOLLEYBALL_HEADER_COPY} from "../constants/headerContent";
import DesktopRailNavigation from "../components/desktop/DesktopRailNavigation";
import { CountryModal, LeagueModal } from "../components/desktop/modals";
import { DatePickerModal } from "../../../../components/dashboards/components/shared/date-picker";
import { ensureISODate } from "../../../../components/dashboards/utils/dateUtils";
import useVolleyballRailFilters from '../../../../components/dashboards/mobile/hooks/useVolleyballRailFilters';

const VolleyballDesktopDashboard = () => {
  const isBrazilian = getBrazilianStatus();

  const t = useCallback(
    (text) => (isBrazilian ? volleyballDashPtBR[text] || text : text),
    [isBrazilian]
  );

  const [activeTab, setActiveTab] = useState('games');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Data state
  const [leagues, setLeagues] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [games, setGames] = useState([]);
  const [standings, setStandings] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);

  // Filter state - MUST be declared before using in hooks
  const [filters, setFilters] = useState({
    date: VolleyballService.getTodayDate(),
    country: '',
    league: '',
    season: ''
  });

  // Handle rail filter changes
  const handleRailFiltersChange = useCallback((nextFilters) => {
    setFilters(prev => ({
      ...prev,
      date: nextFilters.date || prev.date,
      country: nextFilters.country || '',
      league: nextFilters.league || ''
    }));
  }, []);

  // Initialize rail filters hook
  const {
    filters: railFilters,
    activeTab: railActiveTab,
    hasActiveFilters: railHasActiveFilters,
    primaryTabs: railPrimaryTabs,
    filterObjects: railFilterObjects,
    handleTabChange: handleRailTabChange,
    handleFilterClick,
    updateFilter: updateRailFilter,
    resetFilters: resetRailFilters,
    showDateSheet: showDateModal,
    setShowDateSheet: setShowDateModal,
    showCountrySheet: showCountryModal,
    setShowCountrySheet: setShowCountryModal,
    showLeagueSheet: showLeagueModal,
    setShowLeagueSheet: setShowLeagueModal
  } = useVolleyballRailFilters({
    initialDate: filters.date,
    onFiltersChange: handleRailFiltersChange
  });

  // Filter change handler
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (activeTab === 'games') {
      setGamesPagination(prev => ({ ...prev, currentPage: 1 }));
      setSelectedGame(null);
    } else if (activeTab === 'teams') {
      setTeamsPagination(prev => ({ ...prev, currentPage: 1 }));
    }
  }, [activeTab]);

  // Page change handlers
  const handleGamesPageChange = useCallback((page) => {
    setGamesPagination(prev => ({ ...prev, currentPage: page }));
  }, []);

  const handleTeamsPageChange = useCallback((page) => {
    setTeamsPagination(prev => ({ ...prev, currentPage: page }));
  }, []);

  // Pagination state for teams
  const [teamsPagination, setTeamsPagination] = useState({
    currentPage: 1,
    perPage: 16,
    totalPages: 1
  });

  // Pagination state for games
  const [gamesPagination, setGamesPagination] = useState({
    currentPage: 1,
    perPage: 16,
    totalPages: 1
  });

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [leaguesRes, seasonsRes] = await Promise.all([
          VolleyballService.getLeagues(),
          VolleyballService.getSeasons()
        ]);
        setLeagues(leaguesRes.response || []);
        setSeasons(seasonsRes.response || []);
      } catch (err) {
        console.error('Failed to load initial data:', err);
      }
    };
    loadInitialData();
  }, []);

  // Load games
  const loadGames = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await VolleyballService.getGames(
        null,
        filters.date,
        filters.league || null,
        null,
        null,
        'UTC'
      );

      let filteredGames = response.response || [];

      if (filters.country) {
        filteredGames = filteredGames.filter(game =>
          game.country?.name === filters.country
        );
      }

      setGames(filteredGames);
      setGamesPagination(prev => ({
        ...prev,
        totalPages: Math.ceil(filteredGames.length / prev.perPage)
      }));
    } catch (err) {
      setError('Failed to load games');
      console.error('Load games error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters.date, filters.league, filters.country]);

  // Load standings
  const loadStandings = useCallback(async () => {
    if (!filters.league || !filters.season) {
      setStandings([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await VolleyballService.getStandings(filters.league, filters.season);
      const standingsData = response.response || [];
      setStandings(Array.isArray(standingsData[0]) ? standingsData[0] : standingsData);
    } catch (err) {
      setError('Failed to load standings');
      console.error('Load standings error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters.league, filters.season]);

  // Load teams
  const loadTeams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filters.country && filters.country !== 'All Countries') params.country = filters.country;
      if (filters.league) params.league = filters.league;
      if (filters.season) params.season = filters.season;

      // Need at least one parameter
      if (Object.keys(params).length === 0) {
        params.country = USA.name; // Default fallback
      }

      const response = await VolleyballService.getTeams(
        null, null, null, params.country, params.league, params.season, null
      );

      const teamsData = response.response || [];
      setTeams(teamsData);
      setTeamsPagination(prev => ({
        ...prev,
        totalPages: Math.ceil(teamsData.length / prev.perPage)
      }));
    } catch (err) {
      setError('Failed to load teams');
      console.error('Load teams error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters.country, filters.league, filters.season]);

  // Load data when filters change
  useEffect(() => {
    if (activeTab === TABS_ID.GAMES) {
      loadGames()
    } else if (activeTab === TABS_ID.STANDINGS) {
      loadStandings()
    } else if (activeTab === TABS_ID.TEAMS) {
      loadTeams()
    }
  }, [activeTab, loadGames, loadStandings, loadTeams]);

  // Refresh data
  const refreshData = useCallback(() => {
    if (activeTab === TABS_ID.GAMES) {
      loadGames();
    }
    else if (activeTab === TABS_ID.STANDINGS) {
      loadStandings();
    }
    else if (activeTab === TABS_ID.TEAMS) {
      loadTeams();
    }
  }, [activeTab, loadGames, loadStandings, loadTeams]);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Add Unified Navigation Header */}
      <UnifiedNavHeader
        title="Volleyball Dashboard"
        showBackButton
      />

      <div className="relative px-6 py-8 pb-24">
        <div className="max-w-[1600px] mx-auto space-y-4">
          {/* Header */}
          <Header t={t} {...VOLLEYBALL_HEADER_COPY} />

          {/* Beta Notice */}
          {/*<BetaNotice t={t} />*/}

          {/* Desktop Rail Navigation - Filter Rail */}
          <DesktopRailNavigation
            sport="volleyball"
            primaryTabs={railPrimaryTabs}
            filters={railFilterObjects}
            activeTab={railActiveTab}
            onTabChange={handleRailTabChange}
            onFilterChange={handleFilterClick}
            onReset={resetRailFilters}
            hasActiveFilters={railHasActiveFilters}
          />

          {/* Navigation */}
          <DesktopNavigation activeTab={activeTab} setActiveTab={setActiveTab} />

          {/* Error Message */}
          {error && (
            <div className="border border-red-500/40 rounded-sm p-4 bg-red-500/10">
              <div className="flex items-center gap-3 text-sm font-mono uppercase tracking-widest text-red-200">
                <AlertCircle className="text-red-300" size={20} />
                <p>{error}</p>
              </div>
            </div>
          )}

          {/* Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === TABS_ID.GAMES && (
                <GamesView
                  games={games}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  leagues={leagues}
                  loading={loading}
                  selectedGame={selectedGame}
                  onGameSelect={setSelectedGame}
                  pagination={gamesPagination}
                  onPageChange={handleGamesPageChange}
                  t={t}
                />
              )}

              {activeTab === TABS_ID.STANDINGS && (
                <StandingsView
                  standings={standings}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  leagues={leagues}
                  seasons={seasons}
                  loading={loading}
                  onRefresh={refreshData}
                  t={t}
                />
              )}

              {activeTab === TABS_ID.TEAMS && (
                <TeamsView
                  teams={teams}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  leagues={leagues}
                  seasons={seasons}
                  loading={loading}
                  onRefresh={refreshData}
                  pagination={teamsPagination}
                  onPageChange={handleTeamsPageChange}
                  t={t}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Desktop Filter Modals */}
      <DatePickerModal
        isOpen={showDateModal}
        onClose={() => setShowDateModal(false)}
        selectedDate={ensureISODate(railFilters.date || filters.date)}
        onDateSelect={(isoDate) => {
          const formatted = ensureISODate(isoDate, filters.date);
          updateRailFilter("date", formatted);
          setFilters((prev) => ({ ...prev, date: formatted }));
          setShowDateModal(false);
        }}
      />

      <CountryModal
        isOpen={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        selectedCountry={railFilters.country}
        onCountrySelect={(country) => {
          updateRailFilter('country', country);
          setShowCountryModal(false);
        }}
        countries={[]}
      />

      <LeagueModal
        isOpen={showLeagueModal}
        onClose={() => setShowLeagueModal(false)}
        selectedLeague={railFilters.league}
        onLeagueSelect={(league) => {
          updateRailFilter('league', league);
          setShowLeagueModal(false);
        }}
        leagues={leagues}
      />
    </div>
  );
}

export default VolleyballDesktopDashboard;
